import {
  getMaxBenefitBenefitPerPeriodFields,
  deathBenefitAtAge,
  policyBenefitPeriodMonths,
  policyMaximumBenefitAmount,
  policyTotalLTCBalance,
  isPrimaryPolicyHolder,
  expectedPolicyActivationAge,
  monthlyBenefitWithdrawlRate,
  hasCobRider,
  deathBenefitFaceAmount,
  policyMinimumGuaranteedBenefitAmount,
  policyContinuationBenefitAmount,
  policyTotalLTCBenefitAmount,
  policyInflationProtectionPercentage,
  policyInflationProtectionType,
  commonFields,
  MaxBenefitPeriod,
  fieldOverride,
  policyLumpSumPayment,
  policyLimitedPayYears,
  policyPremiumMonthlyCost,
  homeCareBenefitPeriodMonths,
  homeCareWaitingPeriodDays,
  policyPremiumIncreaseRate,
  policyPremiumIncreaseYears,
  facilityCareWaitingPeriodDays,
  policyPremiumStartYear,
  PolicyPromptField,
  policyPaymentType,
  policyIndemnityBenefitPercent,
  policyInflationProtectionOnCOBAmount,
  policyInflationProtectionOnFaceAmount,
  policyInflationProtectionYears,
  policyAccelerationBenefitDurationMonths,
  policyEobBenefitDurationMonths,
  policyBaseBenefitAmountAtIssue,
  policyContinuationBenefitAmountAtIssue,
  policyBaseBenefitGuaranteedInitialRate,
  policyBaseBenefitGuaranteedInitialRateYears,
  policyBaseBenefitGuaranteedRenewalRate,
  policyMonthlyContinuationBenefitChargeRate,
  policyAccumulatedValueAtIssue,
} from './policyUploadFields';
import { FundingPolicyType } from '../appModel';
import { fundingPolicyTypeDefList } from '../FundingPolicyTypeDef';

/**
 * Field-map authoring rule:
 * Override descriptions must generalize to the provided illustration, not the
 * sample PDFs in our docs. Do not include sample-specific dollar values,
 * contract years, page labels, or "sample illustration" references. Prefer
 * product concepts and relationship checks that can survive modest template
 * changes. Mention a fixed value only when it is a product-invariant extraction
 * rule for every matching policy.
 */

const hybridLifeIraCommonFields = commonFields.filter(
  field =>
    field.fieldId !== 'policyLumpSumPayment' &&
    field.fieldId !== 'policyLimitedPayYears' &&
    field.fieldId !== 'policyPremiumMonthlyCost' &&
    field.fieldId !== 'policyPaymentType',
);

const hybridLifeIraFields = [
  fieldOverride(policyLumpSumPayment, {
    fieldDescription: `This value will depend on Has COB Rider Option. If "Has COB Rider Option" is yes, then look for the exact row label "1st Year Premium" associated to the column "Base Policy + COB Rider". This amount should be more than the 1st year premium for just the base policy. If "Has COB Rider Option" is no, then get the "1st Year Premium" associated to the column "Base Policy".
    This data should be located next to "Subsequent COB Premium" amount. Importantly, DO NOT return the "Contract Premium" amount which is found in the header of several pages.`,
  }),
  fieldOverride(policyLimitedPayYears, {
    fieldDescription: `Look for the number of payments needed for the COB rider. It will be in the format "COB X-Pay Rider"`,
  }),
  fieldOverride(policyPremiumMonthlyCost, {
    fieldDescription: `Look for the "Subsequent COB Premium" which should be annual. If so, divide this amount by 12 to get the monthly premium amount.`,
  }),
];

const hybridLifeFields = [
  deathBenefitFaceAmount,
  policyMinimumGuaranteedBenefitAmount,
  policyContinuationBenefitAmount,
  policyTotalLTCBenefitAmount,
  ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
  ...commonFields,
  policyInflationProtectionPercentage,
  policyInflationProtectionType,
];

// Securian SecureCare III / IV share one field map — the two products come from
// the same Securian proposal template, and both are illustration-verified (see
// calculations/docs/securian-securecare/securecare-iv-gap-analysis.md). The
// acceleration + extension DURATION MONTHS drive secureCareStrategy's
// duration-derived pools (Monthly Benefit × months); the dollar Total /
// Extension fields remain as the all-or-nothing fallback for policies uploaded
// before durations were captured.
//
// Descriptions anchor on labels and product concepts, never on page number or
// ordinal position, because the two products' layouts differ: IV runs a page
// longer, labels its benefit row "Day 1: cash indemnity benefits for LTC" where
// III says "Day 1: LTC benefits", and runs 7 years (2+5) against III's 6 (2+4).
// Any anchor tied to one product's layout misreads the other.
// Unlike hybridLifeIraCommonFields, policyPaymentType is NOT filtered out here:
// there is no SecureCare override for it, so filtering would drop the field
// entirely. It describes payout mode (indemnity vs reimbursement), which is
// orthogonal to the premium-timing overrides below.
const secureCareCommonFields = commonFields.filter(
  field =>
    field.fieldId !== 'policyLumpSumPayment' &&
    field.fieldId !== 'policyLimitedPayYears' &&
    field.fieldId !== 'policyPremiumMonthlyCost',
);

const secureCareFields = [
  fieldOverride(deathBenefitFaceAmount, {
    fieldTitle: 'Face Amount/Death Benefit',
    fieldDescription: `The policy's Face Amount — the death benefit paid to beneficiaries, which can also be accessed early to fund qualifying long-term care. Look for a row labelled "Face amount" in the proposal's main summary table, listed alongside the Elimination period and the guaranteed minimum death benefit. DISAMBIGUATION: the proposal states a Face Amount more than once. Ignore any occurrence appearing inside a "Detailed premium report" or a first-year premium breakdown — that figure is a premium allocation, not the policy's face amount. If more than one candidate remains, prefer the one in the coverage summary table. Return null if no face amount is stated.`,
  }),
  fieldOverride(policyMinimumGuaranteedBenefitAmount, {
    fieldDescription: `The minimum guaranteed death benefit that will pay out upon the policyholder's passing even if the death benefit was exhausted to pay for long-term care costs.`,
  }),
  // The Total LTC Benefit and the Extension (COB) dollar amount are deliberately
  // NOT extracted for SecureCare. Both are products of the two durations and the
  // Day 1 monthly benefit, which secureCareStrategy derives itself
  // (deriveAtIssuePools). Extracting them independently produced a third figure
  // that disagreed with the pools the projection actually uses — and, via the
  // legacy `COB = total − face` post-process, a COB that was simply wrong.
  fieldOverride(policyAccelerationBenefitDurationMonths, {
    fieldTitle: 'Acceleration for LTC Benefit Duration Months',
    fieldDescription: `Capture the Acceleration for LTC Benefit duration in months — how many months the Acceleration (base) LTC pool pays out at the maximum monthly benefit before it is exhausted. It is usually stated in the benefit-guarantee diagram as a number of years labelled for the Acceleration pool (e.g. "N years acceleration"); convert to months as N × 12. DO NOT use the total LTC benefit period, the Extension period, or the PREMIUM PAYMENT period — proposals often state a premium payment duration in years on the same page (e.g. "premium paid over N years"), and that is not a benefit duration. Return null if no Acceleration period is stated.`,
  }),
  fieldOverride(policyEobBenefitDurationMonths, {
    fieldTitle: 'Extension of LTC Benefit Duration Months',
    fieldDescription: `Capture the Extension of LTC Benefit duration in months — how many months the Extension pool pays out at the maximum monthly benefit after the Acceleration pool is exhausted. It is usually stated in the benefit-guarantee diagram as a number of years labelled for the Extension pool (e.g. "N years extension"); convert to months as N × 12. If the proposal states the total LTC benefit period and the Acceleration period but not the Extension period, derive it as the total minus the Acceleration period. DO NOT use the total benefit period itself, the Acceleration period, or the PREMIUM PAYMENT period (e.g. "premium paid over N years"). Return null if neither an Extension period nor a total benefit period is stated.`,
  }),
  fieldOverride(policyLumpSumPayment, {
    fieldDescription: `The single premium, when the proposal states one — look for wording such as "$X single premium paid". Return null when premiums are instead paid over multiple years; in that case populate the monthly premium and the pay-years fields instead.`,
  }),
  fieldOverride(policyPremiumMonthlyCost, {
    fieldDescription: `The monthly premium, when premiums are paid over multiple years. The proposal states an annual premium (e.g. "Annual premium: $X" alongside "$Y premium paid over N years"); divide that annual figure by 12. Enter 0 when the proposal states a single premium — that amount belongs in the Lump-Sum Payment field instead.`,
  }),
  fieldOverride(policyLimitedPayYears, {
    fieldDescription: `The number of years over which premiums are paid, from wording such as "$X premium paid over N years" — return N. Return null when the proposal states a single premium. This is a PREMIUM duration; do not confuse it with the total LTC benefit period or the Acceleration/Extension benefit durations.`,
  }),
  ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
  ...secureCareCommonFields,
  fieldOverride(policyInflationProtectionPercentage, {
    fieldTitle: 'LTC Inflation Protection Agreement',
  }),
  policyInflationProtectionType,
];

// CareMatters II / Together size the ENTIRE LTC pool as `max monthly benefit ×
// benefit period months` — cmTwoStrategy opens `extensionOfBenefitsPool: 0` and
// never draws the continuation amount as a separate pool. So this duration spans
// the total LTC benefit (specified amount + continuation of benefits), NOT just
// the accelerated specified amount. That is the opposite of SecureCare, where the
// Acceleration and Extension periods are captured separately.
//
// The stock policyBenefitPeriodMonths description is written for Short Term Care
// ("a key feature to Short Term Care policies") and would prime the model onto
// the wrong concept here, so the wording is replaced rather than reused.
//
// Scope note: this covers WHERE TO FIND the value. Why the calc needs it lives in
// policyFieldCalculationContext (prompt STEP 2b) — both reach the same prompt, so
// keep them complementary rather than restating each other.
const careMattersBenefitPeriodMonths = fieldOverride(policyBenefitPeriodMonths, {
  fieldTitle: 'Long-Term Care Benefit Period Months',
  fieldDescription: `How many months the policy pays long-term care benefits. Documents usually state this as a benefit period in whole years — commonly 2 to 7 — with varying wording: a specified long-term care benefit period, an LTC benefit duration, or how long the maximum monthly benefit is payable. Convert years to months (N × 12). Return the period covering the total long-term care benefit, not a shorter acceleration or first-phase period. If no period is stated, or to check one you found, divide the total long-term care benefit by the maximum monthly long-term care benefit — but only using figures that EXCLUDE inflation. Documents often also print a larger Day 1 or current-year total that already includes inflation-rider growth; dividing that one yields too many months. Prefer a stated period over a derived one. Do not return the premium payment period (a single premium, or premiums paid over N years) — that is not a benefit duration. Return null only if no period is stated and no un-inflated total and monthly pair is available.`,
});

export const policyTypeFieldMap = {
  [FundingPolicyType.hybridLifeInsurance]: hybridLifeFields,
  // Lincoln MoneyGuard Fixed Advantage runs the generic hybrid-life calc, but
  // caps cash indemnity at 80% of the monthly max — a fixed product property,
  // like Asset Care's 75%. Mirror Asset Care: the same hybrid fields plus a
  // fixed indemnity-cap override so the upload always records 0.80, which the
  // lincolnMoneyGuardStrategy then applies under an indemnity election.
  [FundingPolicyType.moneyGuardFixedAdvantage]: [
    ...hybridLifeFields,
    fieldOverride(policyIndemnityBenefitPercent, {
      fieldTitle: 'Indemnity Benefit Cap (% of Monthly Limit)',
      fieldDescription: `MoneyGuard caps cash indemnity at 80% of the Maximum Monthly LTC Benefit Amount. Always return 0.80 for MoneyGuard, regardless of whether the illustration is shown in reimbursement or indemnity mode — the cap is a fixed property of the product.`,
    }),
  ],
  [FundingPolicyType.longTermCareInsurance]: [
    policyMaximumBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
    policyInflationProtectionYears,
    policyPremiumIncreaseRate,
    policyPremiumIncreaseYears,
  ],
  [FundingPolicyType.lifeInsuranceWithRider]: [
    deathBenefitFaceAmount,
    policyMinimumGuaranteedBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
  ],
  [FundingPolicyType.shortTermCareInsurance]: [
    policyBenefitPeriodMonths,
    policyMaximumBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
  ],
  // ManhattanLife OmniFlex STC: day-denominated short-term care. calculationType
  // is the type itself, so without this entry getPolicyPromptFields would throw.
  // Mirrors the generic short-term care fields MINUS policyMaximumBenefitAmount:
  // OmniFlex has no lifetime dollar pool (coverage is bounded by day counters),
  // so extracting a "Max Benefit" would invite a dollar figure the engine
  // ignores. isFast50Election is deliberately NOT extracted — it is a modeling
  // election, not a document attribute. homeCareBenefitPeriodMonths IS
  // extracted: the HHC rider elects its own benefit period, and without it the
  // projection seeds the home track from the facility period.
  [FundingPolicyType.manhattanLifeOmniFlexStc]: [
    policyBenefitPeriodMonths,
    homeCareBenefitPeriodMonths,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
  ],
  [FundingPolicyType.annuityHybrid]: [
    policyTotalLTCBalance,
    ...getMaxBenefitBenefitPerPeriodFields([
      MaxBenefitPeriod.daily,
      MaxBenefitPeriod.monthly,
      MaxBenefitPeriod.annual,
    ]),
    ...commonFields,
  ],
  [FundingPolicyType.jointHybridAssetBased]: [
    deathBenefitFaceAmount,
    policyMinimumGuaranteedBenefitAmount,
    policyContinuationBenefitAmount,
    policyTotalLTCBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
  ],
  [FundingPolicyType.jointHybridAnnuityBased]: [
    isPrimaryPolicyHolder,
    expectedPolicyActivationAge,
    policyTotalLTCBalance,
    ...getMaxBenefitBenefitPerPeriodFields([
      MaxBenefitPeriod.daily,
      MaxBenefitPeriod.monthly,
      MaxBenefitPeriod.annual,
    ]),
    ...commonFields,
  ],
  [FundingPolicyType.lifeWithIndexAccount]: [
    deathBenefitAtAge,
    monthlyBenefitWithdrawlRate,
    policyMinimumGuaranteedBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...commonFields,
  ],
  [FundingPolicyType.hybridLifeIra]: [
    hasCobRider,
    policyTotalLTCBalance,
    {
      ...policyContinuationBenefitAmount,
      fieldDescription: '',
    },
    {
      ...monthlyBenefitWithdrawlRate,
      fieldDescription: '',
    },
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...hybridLifeIraCommonFields,
    ...hybridLifeIraFields,
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option Base',
      fieldDescription: `This is the inflation protection percentage associated with the Base amount. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option Rider',
      fieldDescription: `This is the inflation protection percentage associated with the Rider. If the policy does not have this option, then return 0%.`,
    }),
    policyInflationProtectionType,
  ],
  [FundingPolicyType.jointHybridLifeIra]: [
    hasCobRider,
    isPrimaryPolicyHolder,
    expectedPolicyActivationAge,
    policyTotalLTCBalance,
    {
      ...policyContinuationBenefitAmount,
      fieldDescription: '',
    },
    {
      ...monthlyBenefitWithdrawlRate,
      fieldDescription: '',
    },
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    ...hybridLifeIraCommonFields,
    ...hybridLifeIraFields,
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option Base',
      fieldDescription: `This is the inflation protection percentage associated with the Base amount. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option Rider',
      fieldDescription: `This is the inflation protection percentage associated with the Rider. If the policy does not have this option, then return 0%.`,
    }),
    policyInflationProtectionType,
  ],
  [FundingPolicyType.nylSecureCare]: [
    policyMaximumBenefitAmount,
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily]),
    policyPremiumStartYear,
    policyPremiumMonthlyCost,
    fieldOverride(policyInflationProtectionPercentage, {
      fieldDescription: `If the stated inflation protection begins with "CPI-U" and is not a number like 3% or 5%, then return 0% inflation protection. Similarly, if there is no mention of inflation protection on the same table as the premium, then return 0%.`,
    }),
    fieldOverride(policyPremiumIncreaseRate, {
      fieldDescription: `This value is the same as the policy's inflation protection percentage. If there is no inflation protection, return 0%.`,
    }),
    fieldOverride(homeCareWaitingPeriodDays, {
      fieldDescription: `Always set this value to 20 days. For location, write that we assume the policyholder will use the care-coordination benefit which reduces the home waiting period to 20 days.`,
    }),
    fieldOverride(facilityCareWaitingPeriodDays, {
      fieldTitle: 'Facility Waiting Period',
      fieldDescription: `This value is labled as just "Waiting Period". It is most commonly 90 days. It is the period during which the policyholder must cover care costs out-of-pocket before insurance benefits begin. `,
    }),
  ],
  [FundingPolicyType.nylJointSecureCare]: [
    fieldOverride(policyMaximumBenefitAmount, {
      fieldDescription: `Look for the exact label "Policy Lifetime Maximum". Take this amount (for either Applicant 1 or 2) and multiply it by 3. In the location description, write "Individual lifetime maximum multiplied by 3, assuming shared care rider." We multiply by three because each partner has their own policy with the same maximum benefit amount and the shared care rider provides a third pool of equal value. The stored value must be the COMBINED household total for both spouses (both individual pools plus the shared pool) — never a single spouse's individual maximum, which the calculation would otherwise understate 3x.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily]),
    policyPremiumStartYear,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `Use the amount under the "Total" column that combines both applicants' "Total Premium" amounts. Do not use the "Total Premium" of just one applicant.`,
    }),
    fieldOverride(policyInflationProtectionPercentage, {
      fieldDescription: `If the stated inflation protection begins with "CPI-U" and is not a number like 3% or 5%, then return 0% inflation protection. Similarly, if there is no mention of inflation protection on the same table as the premium, then return 0%.`,
    }),
    fieldOverride(policyPremiumIncreaseRate, {
      fieldDescription: `This value is the same as the policy's inflation protection percentage. If there is no inflation protection, return 0%.`,
    }),
    fieldOverride(homeCareWaitingPeriodDays, {
      fieldDescription: `Always set this value to 20 days. For location, write that we assume the policyholder will use the care-coordination benefit which reduces the home waiting period to 20 days.`,
    }),
    fieldOverride(facilityCareWaitingPeriodDays, {
      fieldTitle: 'Facility Waiting Period',
      fieldDescription: `This value is labled as just "Waiting Period". It is most commonly 90 days. It is the period during which the policyholder must cover care costs out-of-pocket before insurance benefits begin. `,
    }),
  ],
  [FundingPolicyType.nylAssetFlex]: [
    fieldOverride(deathBenefitFaceAmount, {
      fieldTitle: 'LTC Acceleration Benefit',
      fieldDescription: `The LTC Acceleration Benefit is the amount available for reimbursement of expenses associated with Qualified LTC Services. It should be similar or equal to the intial face amount.`,
    }),
    fieldOverride(policyTotalLTCBenefitAmount, {
      fieldDescription: `The Total LTC Benefit Amount is the maximum amount that can be accessed to fund long-term care. This amount should equal the LTC Acceleration Benefit plus the Extension of Benefit amount.`,
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldTitle: `Extension of Benefits (EoB) Amount`,
      fieldDescription: `The Extension of Benefits Amount (EoB Amount), is a separate pool from the Face Amount which can be accessed to fund long-term care.`,
    }),
    fieldOverride(policyAccelerationBenefitDurationMonths, {
      fieldTitle: 'LTC Acceleration Benefit Duration Months',
      fieldDescription: `Capture the LTC Acceleration Benefit duration in months — how many months the LTC Acceleration Benefit pool pays out at the monthly maximum benefit before it is exhausted. Use the policy design, coverage summary, or benefit-period wording for the Acceleration/base layer, not the Extension of Benefits layer. If the period is stated in another unit, convert to months only when the conversion is explicit and unambiguous. Return null if the illustration does not state this period.`,
    }),
    fieldOverride(policyEobBenefitDurationMonths, {
      fieldTitle: 'Extension of Benefits Duration Months',
      fieldDescription: `Capture the Extension of Benefits (EoB) duration in months — how many months the Extension of Benefits pool pays out at the monthly maximum benefit after the LTC Acceleration Benefit is exhausted. Use the Extension of Benefits or continuation period for the second LTC pool, not the Acceleration/base layer. If the period is stated in another unit, convert to months only when the conversion is explicit and unambiguous. Return null if the illustration does not state this period.`,
    }),
    fieldOverride(policyMinimumGuaranteedBenefitAmount, {
      fieldTitle: 'Residual Death Benefit',
      fieldDescription: `The residual death benefit is 10% of the "Initial Face Amount" (found under "POLICY COMPONENTS" on the first few pages).`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(homeCareWaitingPeriodDays, {
      fieldDescription: `Assume a 0-day waiting period for home care since the policyholder will use the care-coordination benefit.`,
    }),
    fieldOverride(facilityCareWaitingPeriodDays, {
      fieldDescription: `This is typically always 90 days. Details can be found in the Narrative Summary.`,
    }),
    policyPremiumStartYear,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldTitle: 'Annual Premium (converted to monthly)',
      fieldDescription: `Look for the text "Annual Planned Premium Amount". Divide this amount by 12 to get the monthly premium. If single pay, enter 0.`,
    }),
    fieldOverride(policyLimitedPayYears, {
      fieldTitle: 'X-Pay Annual Premium Payments',
      fieldDescription: `If premium is annual pay, look for the text "X-Pay Annual Planned Premium" and return the number associated to X (e.g 5 or 10). If single-pay, leave blank.`,
    }),
    fieldOverride(policyLumpSumPayment, {
      fieldTitle: 'Single-Pay Planned Premium',
      fieldDescription: `Look for the text "Single-Pay Planned Premium" under the section "PREMIUM AND OTHER INFORMATION". If premium is annual-pay, this field should be left blank.`,
    }),
    fieldOverride(policyInflationProtectionPercentage, {
      fieldTitle: 'Automatic Compounding Inflation Option',
      fieldDescription: `This value is the "Automatic Compounding Inflation Option" (ACIO) percentage found under Policy Components. If the policy does not have this option, then return 0.`,
    }),
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'ACIO Applied to LTC Acceleration (Monthly Benefit for LTC)',
      fieldDescription: `The inflation percentage that applies to the LTC Acceleration layer — i.e. how much the "Monthly Benefit for LTC" grows each year. On Asset Flex the ACIO does NOT always inflate this layer, so determine it per illustration using BOTH signals: (1) the ACIO / inflation definition text under Policy Components or the Narrative Summary — does it state the ACIO applies to the LTC Acceleration / base benefit, or only to the Extension of Benefits?; and (2) the Yearly Detail ledger "Monthly Benefit for LTC" column — if that column stays level across every year, the Acceleration layer does not inflate, so return 0%; if it steps up year over year, return the per-year compound rate (it should equal the ACIO percentage; you can confirm via row2 / row1 - 1). If the two signals disagree, trust the Yearly Detail ledger. Do not assume Asset Flex always — or never — inflates this layer.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'ACIO Applied to Extension of Benefits (Monthly Benefit for EOB)',
      fieldDescription: `The inflation percentage that applies to the Extension of Benefits (EoB) layer — i.e. how much the "Monthly Benefit for EOB" grows each year. Determine it per illustration using BOTH signals: (1) the ACIO / inflation definition text — does it state the ACIO applies to the Extension of Benefits / continuation layer?; and (2) the Yearly Detail ledger "Monthly Benefit for EOB" column — if that column stays level across every year, return 0%; if it steps up year over year, return the per-year compound rate (it should equal the ACIO percentage; confirm via row2 / row1 - 1). If the two signals disagree, trust the Yearly Detail ledger.`,
    }),
    fieldOverride(policyInflationProtectionYears, {
      fieldTitle: 'ACIO Inflation Duration (Years)',
      fieldDescription: `The number of years the ACIO benefit increases compound before leveling off. Read this from the Yearly Detail ledger: count how many years the inflating monthly-benefit column keeps increasing before it goes flat. If the inflating monthly benefit keeps increasing every year through the end of the ledger (lifetime / uncapped inflation), return null. Return null if no inflation applies to either layer.`,
    }),
  ],
  // SecureCare III / IV share one field map (see `secureCareFields`). Duration
  // months feed secureCareStrategy's duration-derived pools; the dollar
  // Total/Extension fields stay as the legacy fallback.
  [FundingPolicyType.secureCareThree]: secureCareFields,
  [FundingPolicyType.secureCareFour]: secureCareFields,
  [FundingPolicyType.assetCare]: [
    deathBenefitFaceAmount,
    fieldOverride(policyContinuationBenefitAmount, {
      fieldTitle: 'Continuation of Benefits',
      fieldDescription: `The Continuation of Benefits Amount (CoB Amount) is a separate pool from the Face Amount which can be accessed to fund long-term care. For this policy, look for the keyword "Unlimited". If present, return "unlimited" even if there is an actual amount listed. If not, return the actual amount.`,
    }),
    fieldOverride(policyAccelerationBenefitDurationMonths, {
      fieldTitle: 'AOB (Acceleration of Benefits) Benefit Period Months',
      fieldDescription: `Capture the Acceleration of Benefits (AOB) benefit period in months — how many months the base Face Amount / Acceleration of Benefits pool pays out at the monthly maximum benefit before it is exhausted. This is the base LTC benefit period (often shown as a number of years on the policy design or "How does Asset Care work?" pages), NOT the Continuation of Benefits period. If the period is stated in years, convert to months only when the conversion is explicit and unambiguous (e.g. 2 years = 24 months). Return null if the illustration does not state this period.`,
    }),
    fieldOverride(policyEobBenefitDurationMonths, {
      fieldTitle: 'COB (Continuation of Benefits) Benefit Period Months',
      fieldDescription: `Capture the Continuation of Benefits (CoB) benefit period in months — how many months the Continuation of Benefits pool pays out at the monthly maximum benefit after the Acceleration of Benefits pool is exhausted. Use the Continuation of Benefits / continuation period for the second LTC pool, not the Acceleration/base layer. If the Continuation of Benefits is "Unlimited" or Lifetime, return null (there is no finite period). If the period is stated in years, convert to months only when the conversion is explicit and unambiguous. Return null if the illustration does not state a finite period.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(homeCareWaitingPeriodDays, {
      fieldTitle: 'Elimination Period',
      fieldDescription: `Look for the "Elimination Period" section under the "How does Asset Care work?" page. This is the period during which the policyholder must cover care costs out-of-pocket for Home Health Care before insurance benefits begin.`,
    }),
    fieldOverride(facilityCareWaitingPeriodDays, {
      fieldTitle: 'Elimination Period',
      fieldDescription: `Look for the "Elimination Period" section under the "How does Asset Care work?" page. This is the period during which the policyholder must cover care costs out-of-pocket for all services beside Home Health Care before insurance benefits begin.`,
    }),
    policyPremiumStartYear,
    policyPremiumMonthlyCost,
    policyLimitedPayYears,
    policyLumpSumPayment,
    fieldOverride(policyLumpSumPayment, {
      fieldTitle: 'Total initial premium',
    }),
    policyPaymentType,
    fieldOverride(policyPaymentType, {
      fieldTitle: 'Payment Type (Reimbursement or Indemnity)',
      fieldDescription: `Asset Care supports both reimbursement and cash indemnity (with the ability to switch as needs change). Look for an explicit Payment Type selection on the illustration's Policy Design or Summary pages. If the illustration does not state the elected mode, default to "reimbursement" — most illustrations are produced under the reimbursement assumption. Return "reimbursement" or "indemnity".`,
    }),
    fieldOverride(policyIndemnityBenefitPercent, {
      fieldTitle: 'Indemnity Benefit Cap (% of Monthly Limit)',
      fieldDescription: `Asset Care caps cash indemnity at 75% of the monthly benefit limit (Acceleration of Benefits rider only). Always return 0.75 for Asset Care, regardless of whether the illustration is shown in reimbursement or indemnity mode — the cap is a fixed property of the product.`,
    }),
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option AOB',
      fieldDescription: `This is the inflation protection percentage associated with the AOB. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option COB',
      fieldDescription: `This is the inflation protection percentage associated with the COB. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionYears, {
      fieldTitle: 'Inflation Duration',
      fieldDescription:
        'This is the number of years the policy benefit amounts will inflate. If this is Lifetime, then return "Lifetime". Otherwise, return the number of years.',
    }),
  ],
  [FundingPolicyType.careMattersTwo]: [
    fieldOverride(deathBenefitFaceAmount, {
      fieldTitle: 'Specified amount (amount accerlerated for long-term care)',
    }),
    fieldOverride(policyTotalLTCBenefitAmount, {
      fieldTitle: 'Total long-term care benefit',
      fieldDescription: `This is Day 1 total amount available for long-term care expenses. Make sure you extract the Day 1 amount. It is the sum of the specified amount and the continuation of benefits amount.`,
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldDescription: `This amount is not found within the table on page 2. However, it can be calculated by subtracting the specified amount from the total long-term care benefit.`,
    }),
    fieldOverride(policyMinimumGuaranteedBenefitAmount, {
      fieldDescription: `The minimum guaranteed death benefit that will pay out upon the policyholder's passing even if the death benefit was exhausted to pay for long-term care costs.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    careMattersBenefitPeriodMonths,
    ...commonFields,
    fieldOverride(policyInflationProtectionPercentage, {
      fieldTitle: 'Inflation protection option',
      fieldDescription: `If the policy has inflation protection, return the percentage. If "declined", then return 0.`,
    }),
    policyInflationProtectionType,
  ],
  [FundingPolicyType.jointAssetCare]: [
    deathBenefitFaceAmount,
    fieldOverride(policyContinuationBenefitAmount, {
      fieldTitle: 'Continuation of Benefits',
      fieldDescription: `The Continuation of Benefits Amount (CoB Amount) is a separate pool from the Face Amount which can be accessed to fund long-term care. For this policy, look for the keyword "Unlimited". If present, return "unlimited" even if there is an actual amount listed. If not, return the actual amount.`,
    }),
    fieldOverride(policyAccelerationBenefitDurationMonths, {
      fieldTitle: 'AOB (Acceleration of Benefits) Benefit Period Months',
      fieldDescription: `Capture the Acceleration of Benefits (AOB) benefit period in months — how many months the base Face Amount / Acceleration of Benefits pool pays out at the monthly maximum benefit before it is exhausted. This is the base LTC benefit period (often shown as a number of years on the policy design or "How does Asset Care work?" pages), NOT the Continuation of Benefits period. If the period is stated in years, convert to months only when the conversion is explicit and unambiguous (e.g. 2 years = 24 months). Return null if the illustration does not state this period.`,
    }),
    fieldOverride(policyEobBenefitDurationMonths, {
      fieldTitle: 'COB (Continuation of Benefits) Benefit Period Months',
      fieldDescription: `Capture the Continuation of Benefits (CoB) benefit period in months — how many months the Continuation of Benefits pool pays out at the monthly maximum benefit after the Acceleration of Benefits pool is exhausted. Use the Continuation of Benefits / continuation period for the second LTC pool, not the Acceleration/base layer. If the Continuation of Benefits is "Unlimited" or Lifetime, return null (there is no finite period). If the period is stated in years, convert to months only when the conversion is explicit and unambiguous. Return null if the illustration does not state a finite period.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(homeCareWaitingPeriodDays, {
      fieldTitle: 'Elimination Period',
      fieldDescription: `Look for the "Elimination Period" section under the "How does Asset Care work?" page. This is the period during which the policyholder must cover care costs out-of-pocket for Home Health Care before insurance benefits begin.`,
    }),
    fieldOverride(facilityCareWaitingPeriodDays, {
      fieldTitle: 'Elimination Period',
      fieldDescription: `Look for the "Elimination Period" section under the "How does Asset Care work?" page. This is the period during which the policyholder must cover care costs out-of-pocket for all services beside Home Health Care before insurance benefits begin.`,
    }),
    policyPremiumStartYear,
    policyPremiumMonthlyCost,
    policyLimitedPayYears,
    policyLumpSumPayment,
    fieldOverride(policyLumpSumPayment, {
      fieldTitle: 'Total initial premium',
    }),
    policyPaymentType,
    fieldOverride(policyPaymentType, {
      fieldTitle: 'Payment Type (Reimbursement or Indemnity)',
      fieldDescription: `Asset Care supports both reimbursement and cash indemnity (with the ability to switch as needs change). Look for an explicit Payment Type selection on the illustration's Policy Design or Summary pages. If the illustration does not state the elected mode, default to "reimbursement" — most illustrations are produced under the reimbursement assumption. Return "reimbursement" or "indemnity".`,
    }),
    fieldOverride(policyIndemnityBenefitPercent, {
      fieldTitle: 'Indemnity Benefit Cap (% of Monthly Limit)',
      fieldDescription: `Asset Care caps cash indemnity at 75% of the monthly benefit limit (Acceleration of Benefits rider only). Always return 0.75 for Asset Care, regardless of whether the illustration is shown in reimbursement or indemnity mode — the cap is a fixed property of the product.`,
    }),
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option AOB',
      fieldDescription: `This is the inflation protection percentage associated with the AOB. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option COB',
      fieldDescription: `This is the inflation protection percentage associated with the COB. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionYears, {
      fieldTitle: 'Inflation Duration',
      fieldDescription:
        'This is the number of years the policy benefit amounts will inflate. If this is Lifetime, then return "Lifetime". Otherwise, return the number of years.',
    }),
  ],
  [FundingPolicyType.jointCareMatters]: [
    fieldOverride(deathBenefitFaceAmount, {
      fieldTitle: 'Specified amount (amount accerlerated for long-term care)',
    }),
    fieldOverride(policyTotalLTCBenefitAmount, {
      fieldTitle: 'Total long-term care benefit',
      fieldDescription: `This is Day 1 total amount available for long-term care expenses. Make sure you extract the Day 1 amount. It is the sum of the specified amount and the continuation of benefits amount.`,
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldDescription: `This amount is not found within the table on page 3. However, it can be calculated by subtracting the specified amount from the total long-term care benefit.`,
    }),
    fieldOverride(policyMinimumGuaranteedBenefitAmount, {
      fieldDescription: `The minimum guaranteed death benefit that will pay out upon the policyholder's passing even if the death benefit was exhausted to pay for long-term care costs.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    careMattersBenefitPeriodMonths,
    ...commonFields,
    fieldOverride(policyInflationProtectionPercentage, {
      fieldTitle: 'Inflation protection option',
      fieldDescription: `If the policy has inflation protection, return the percentage. If "declined", then return 0.`,
    }),
    policyInflationProtectionType,
  ],
  [FundingPolicyType.annuityCareTwo]: [
    fieldOverride(policyTotalLTCBalance, {
      fieldTitle: 'Total LTC Balance',
    }),
    ...getMaxBenefitBenefitPerPeriodFields([
      MaxBenefitPeriod.daily,
      MaxBenefitPeriod.monthly,
      MaxBenefitPeriod.annual,
    ]),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
    policyPremiumMonthlyCost,
    policyLumpSumPayment,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `This policy does not have monthly premium, so return 0.`,
    }),
    fieldOverride(policyPaymentType, {
      fieldDescription: `This is a reimbursement product so return "reimbursement". In the location description, write that the policy states that the LTC Withrawals states that the insured will be paid for qualified LTC expenses, which implies a reimbursement model.`,
    }),
  ],
  [FundingPolicyType.forecareFixedAnnuity]: [
    fieldOverride(policyBaseBenefitAmountAtIssue, {
      fieldTitle: 'Guaranteed Base LTC Pool at Issue',
      fieldDescription: `Capture the first contract-year guaranteed base LTC pool at issue from the provided illustration. This may be presented as the accelerated benefit, contract value, or comparable base LTC value. Use the guaranteed projection at issue, not a current-rate/non-guaranteed projection or a future claim-age row.`,
    }),
    fieldOverride(policyContinuationBenefitAmountAtIssue, {
      fieldTitle: 'Guaranteed Extended LTC Pool at Issue',
      fieldDescription: `Capture the first contract-year guaranteed extension-of-benefits pool at issue from the provided illustration. This may be presented as an extended benefit, extension rider, or comparable continuation LTC value. Keep it separate from the base LTC pool and do not add it into the base value.`,
    }),
    fieldOverride(policyAccumulatedValueAtIssue, {
      fieldTitle: 'Guaranteed Residual Contract Value at Issue',
      fieldDescription: `Capture the first contract-year guaranteed residual annuity value at issue from the provided illustration. This may be presented as contract value, death benefit, accumulated value, or a comparable residual value, and is used for non-LTC annuity value after LTC withdrawals.`,
    }),
    fieldOverride(policyMinimumGuaranteedBenefitAmount, {
      fieldTitle: 'Guaranteed Residual Contract Value at Issue',
      fieldDescription: `Capture the first contract-year guaranteed residual annuity value at issue from the provided illustration. This duplicates policyAccumulatedValueAtIssue for backward compatibility with existing tooling and residual non-LTC payout calculations.`,
    }),
    fieldOverride(policyBaseBenefitGuaranteedInitialRate, {
      fieldTitle: 'Initial Guaranteed Contract Value Crediting Rate',
      fieldDescription: `Capture the minimum guaranteed contract-value interest or crediting rate that applies during the initial guaranteed-rate period in the provided illustration. Return the displayed percentage.`,
    }),
    fieldOverride(policyBaseBenefitGuaranteedInitialRateYears, {
      fieldTitle: 'Initial Guaranteed Crediting Rate End Year',
      fieldDescription: `Capture the final contract year covered by the initial guaranteed contract-value crediting rate in the provided illustration. If the illustration states the period as a duration instead of an end year, return the equivalent final contract year only when that conversion is explicit and unambiguous.`,
    }),
    fieldOverride(policyBaseBenefitGuaranteedRenewalRate, {
      fieldTitle: 'Renewal Guaranteed Contract Value Crediting Rate',
      fieldDescription: `Capture the minimum guaranteed renewal contract-value interest or crediting rate that applies after the initial guaranteed-rate period in the provided illustration. Return the displayed percentage.`,
    }),
    fieldOverride(policyMonthlyContinuationBenefitChargeRate, {
      fieldTitle: 'Monthly LTC Rider Charge Rate',
      fieldDescription: `Capture the monthly LTC rider charge rate against contract value from the provided illustration. Return the standardized decimal monthly rate used by the calculation model: convert a displayed percentage to a decimal rate, and leave a displayed decimal-per-dollar rate as a decimal rate.`,
      expectedDataType: 'Float decimal monthly rate, else null if not found',
    }),
    fieldOverride(policyAccelerationBenefitDurationMonths, {
      fieldTitle: 'Accelerated Benefit Duration Months',
      fieldDescription: `Capture the scheduled accelerated-benefit duration in months from the provided illustration's policy design, coverage summary, or benefit-period section. If the illustration states the period in another unit, convert to months only when the conversion is explicit and unambiguous.`,
    }),
    fieldOverride(policyEobBenefitDurationMonths, {
      fieldTitle: 'Extended Benefit Duration Months',
      fieldDescription: `Capture the scheduled extension-of-benefits duration in months from the provided illustration's policy design, coverage summary, or benefit-period section. If the illustration states the period in another unit, convert to months only when the conversion is explicit and unambiguous.`,
    }),
    fieldOverride(policyTotalLTCBalance, {
      fieldTitle: 'Guaranteed Total LTC Pool at Issue',
      fieldDescription: `Capture the first contract-year guaranteed total LTC pool at issue from the provided illustration for backward compatibility. This should represent the base LTC pool plus the extended LTC pool. Do not use a current-rate/non-guaranteed projection or a future claim-age row.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(homeCareWaitingPeriodDays, {
      fieldDescription: `ForeCare illustrations rarely state the elimination period in the benefit tables — look for it in the rider description, disclosure, and definitions pages. Home care is often waived (0 days) when a plan of care is in place; if the document states such a waiver, return 0. Only return a number of days the document actually states. If the document genuinely does not state an elimination period for home care, return 0 — do not assume or invent a default.`,
    }),
    fieldOverride(facilityCareWaitingPeriodDays, {
      fieldDescription: `ForeCare illustrations rarely state the elimination period in the benefit tables — look for it in the rider description, disclosure, and definitions pages, where it is often stated as a number of days for facility care (for example "an elimination period of 90 days") before benefits begin. Only return a number of days the document actually states. If the document genuinely does not state an elimination period, return 0 — do not assume or invent a default.`,
    }),
    policyPremiumStartYear,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `This policy does not have monthly premium, so return 0.`,
    }),
    policyLumpSumPayment,
    fieldOverride(policyPaymentType, {
      fieldDescription: `This is a reimbursement product so return "reimbursement". In the location description, write that the "About ForeCare" section states that exceess coverage is only available for qualified LTC expenses, which implies a reimbursement model.`,
    }),
  ],
  [FundingPolicyType.jointForecareFixedAnnuity]: [
    fieldOverride(policyTotalLTCBalance, {
      fieldTitle: 'Total LTC Benefit',
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(homeCareWaitingPeriodDays, {
      fieldDescription: `ForeCare illustrations rarely state the elimination period in the benefit tables — look for it in the rider description, disclosure, and definitions pages. Home care is often waived (0 days) when a plan of care is in place; if the document states such a waiver, return 0. Only return a number of days the document actually states. If the document genuinely does not state an elimination period for home care, return 0 — do not assume or invent a default.`,
    }),
    fieldOverride(facilityCareWaitingPeriodDays, {
      fieldDescription: `ForeCare illustrations rarely state the elimination period in the benefit tables — look for it in the rider description, disclosure, and definitions pages, where it is often stated as a number of days for facility care (for example "an elimination period of 90 days") before benefits begin. Only return a number of days the document actually states. If the document genuinely does not state an elimination period, return 0 — do not assume or invent a default.`,
    }),
    policyPremiumStartYear,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `This policy does not have monthly premium, so return 0.`,
    }),
    policyLumpSumPayment,
    fieldOverride(policyPaymentType, {
      fieldDescription: `This is a reimbursement product so return "reimbursement". In the location description, write that the "About ForeCare" section states that exceess coverage is only available for qualified LTC expenses, which implies a reimbursement model.`,
    }),
  ],
  [FundingPolicyType.indexedAnnuityCare]: [
    hasCobRider,
    fieldOverride(policyMaximumBenefitAmount, {
      fieldTitle: 'Guaranteed LTC Benefit Balance at LTC Start',
      fieldDescription: `From the Account Value Summary Based on Guaranteed Rates table, capture the LTC Benefit Balance from the row for the expected LTC-start annuitant age. Use the exact age row when present; if the exact age is not present, use the closest lower annuitant age shown in the Guaranteed Rates table. Do not use the Current or Non-Guaranteed table. This is the base LTC pool used by the projection strategy.`,
    }),
    fieldOverride(policyBaseBenefitAmountAtIssue, {
      fieldTitle: 'Guaranteed LTC Benefit Balance at LTC Start',
      fieldDescription: `Duplicate policyMaximumBenefitAmount: from the Account Value Summary Based on Guaranteed Rates table, capture the LTC Benefit Balance from the row for the expected LTC-start annuitant age. Use the closest lower annuitant age if the exact age is not shown. Do not use Current or Non-Guaranteed values.`,
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldTitle: 'COB Rider LTC Balance',
      fieldDescription: `If the policy has no COB rider, return 0. Otherwise capture the COB Rider LTC Balance from the Base Annuity + Continuation of Benefits Rider summary. Keep this separate from the base LTC Benefit Balance and do not add the two together.`,
    }),
    fieldOverride(policyContinuationBenefitAmountAtIssue, {
      fieldTitle: 'COB Rider LTC Balance',
      fieldDescription: `Duplicate policyContinuationBenefitAmount: if the policy has no COB rider, return 0. Otherwise capture the COB Rider LTC Balance from the Base Annuity + Continuation of Benefits Rider summary. Keep this separate from the base LTC Benefit Balance.`,
    }),
    fieldOverride(policyAccelerationBenefitDurationMonths, {
      fieldTitle: 'Base LTC Benefit Period Months',
      fieldDescription: `Capture the base annuity LTC benefit period in months only when it is explicitly stated. This is a fallback input for deriving the base monthly cap only if the Guaranteed Rates row does not print the LTC Monthly Withdrawal Limit. Do not use it to replace or override a printed LTC Monthly Withdrawal Limit. Return null if the period is not stated.`,
    }),
    fieldOverride(policyEobBenefitDurationMonths, {
      fieldTitle: 'COB Rider Benefit Period Months',
      fieldDescription: `Capture the Continuation of Benefits rider benefit period in months. The projection uses this period to size the COB monthly limit from the COB Rider LTC Balance after the base LTC Benefit Balance is exhausted. Convert another stated unit to months only when explicit and unambiguous. Return null if the period is not stated.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(policyLumpSumPayment, {
      fieldDescription: `This value will depend on Has COB Rider Option. If "Has COB Rider Option" is yes, then look for the exact row label "1st Year Premium" associated to the column "Base Policy + COB Rider". This amount should be more than the 1st year premium for just the base policy. If "Has COB Rider Option" is no, then get the "1st Year Premium" associated to the column "Base Policy".
    This data should be located next to "Subsequent COB Premium" amount. Importantly, DO NOT return the "Contract Premium" amount which is found in the header of several pages.`,
    }),
    fieldOverride(policyLimitedPayYears, {
      fieldDescription: `Look for the number of payments needed for the COB rider. It will be in the format "COB X-Pay Rider"`,
    }),
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `Look for the "Subsequent COB Premium" which should be annual. If so, divide this amount by 12 to get the monthly premium amount.`,
    }),
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option Base',
      fieldDescription: `This is the inflation protection percentage associated with the Base amount. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option Rider',
      fieldDescription: `This is the inflation protection percentage associated with the COB Rider. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionYears, {
      fieldTitle: 'COB Rider Inflation Duration',
      fieldDescription: `Capture the finite number of years that the COB rider inflation option applies. If COB inflation is lifetime, uncapped, or the illustration does not state a finite cap, return null.`,
    }),
    fieldOverride(policyInflationProtectionType, {
      fieldTitle: 'Inflation Type (Rider)',
      fieldDescription: `Inflation protection can be either compound or simple. This should be specified next to the inflation protection percentage. If the policy does not specify, enter null.`,
    }),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
    fieldOverride(policyPaymentType, {
      fieldDescription: `This is a reimbursement product, so return "reimbursement". In the location description, write that LTC withdrawals are for qualified LTC expenses up to the monthly LTC withdrawal limit.`,
    }),
  ],
  [FundingPolicyType.assetcareThree]: [
    hasCobRider,
    fieldOverride(policyTotalLTCBalance, {
      fieldTitle: 'Death Benefit/LTC Benefit Balance',
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldDescription: '',
    }),
    fieldOverride(monthlyBenefitWithdrawlRate, {
      fieldDescription: '',
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(policyLumpSumPayment, {
      fieldDescription: `This value will depend on Has COB Rider Option. If "Has COB Rider Option" is yes, then look for the exact row label "1st Year Premium" associated to the column "Base Policy + COB Rider". This amount should be more than the 1st year premium for just the base policy. If "Has COB Rider Option" is no, then get the "1st Year Premium" associated to the column "Base Policy".
    This data should be located next to "Subsequent COB Premium" amount. Importantly, DO NOT return the "Contract Premium" amount which is found in the header of several pages.`,
    }),
    fieldOverride(policyLimitedPayYears, {
      fieldDescription: `Look for the number of payments needed for the COB rider. It will be in the format "COB X-Pay Rider"`,
    }),
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `Look for the "Subsequent COB Premium" which should be annual. If so, divide this amount by 12 to get the monthly premium amount.`,
    }),
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option Base',
      fieldDescription: `This is the inflation protection percentage associated with the Base amount. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option Rider',
      fieldDescription: `This is the inflation protection percentage associated with the COB Rider. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionType, {
      fieldTitle: 'Inflation Type (Rider)',
      fieldDescription: `Inflation protection can be either compound or simple. This should be specified next to the inflation protection percentage. If the policy does not specify, enter null.`,
    }),
    policyPaymentType,
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
  ],
  [FundingPolicyType.jointAssetcareThree]: [
    hasCobRider,
    fieldOverride(policyTotalLTCBalance, {
      fieldTitle: 'Total Death Benefit/LTC Benefit Balance',
    }),
    fieldOverride(policyContinuationBenefitAmount, {
      fieldDescription: '',
    }),
    fieldOverride(monthlyBenefitWithdrawlRate, {
      fieldDescription: '',
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    fieldOverride(policyLumpSumPayment, {
      fieldDescription: `This value will depend on Has COB Rider Option. If "Has COB Rider Option" is yes, then look for the exact row label "1st Year Premium" associated to the column "Base Policy + COB Rider". This amount should be more than the 1st year premium for just the base policy. If "Has COB Rider Option" is no, then get the "1st Year Premium" associated to the column "Base Policy".
    This data should be located next to "Subsequent COB Premium" amount. Importantly, DO NOT return the "Contract Premium" amount which is found in the header of several pages.`,
    }),
    fieldOverride(policyLimitedPayYears, {
      fieldDescription: `Look for the number of payments needed for the COB rider. It will be in the format "COB X-Pay Rider"`,
    }),
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `Look for the "Subsequent COB Premium" which should be annual. If so, divide this amount by 12 to get the monthly premium amount.`,
    }),
    fieldOverride(policyInflationProtectionOnFaceAmount, {
      fieldTitle: 'Inflation Option Base',
      fieldDescription: `This is the inflation protection percentage associated with the Base amount. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionOnCOBAmount, {
      fieldTitle: 'Inflation Option Rider',
      fieldDescription: `This is the inflation protection percentage associated with the COB Rider. If the policy does not have this option, then return 0%.`,
    }),
    fieldOverride(policyInflationProtectionType, {
      fieldTitle: 'Inflation Type (Rider)',
      fieldDescription: `Inflation protection can be either compound or simple. This should be specified next to the inflation protection percentage. If the policy does not specify, enter null.`,
    }),
    policyPaymentType,
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
  ],
  [FundingPolicyType.noLapseGuaranteeLife]: [
    fieldOverride(deathBenefitFaceAmount, {
      fieldTitle: 'Long-Term Care Rider Specified Amount',
    }),
    fieldOverride(policyLumpSumPayment, {
      fieldTitle: 'Initial Planned Premium',
    }),
    fieldOverride(policyMinimumGuaranteedBenefitAmount, {
      fieldTitle: 'Residual Death Benefit',
      fieldDescription: `The policy states in the Residual Death Benefit Endorsement that the residual death benefit is 10% of the initial specified amount.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
    policyPremiumMonthlyCost,
    policyLimitedPayYears,
    fieldOverride(policyInflationProtectionPercentage, {
      fieldDescription:
        'This policy does not have inflation protection, so return 0. In the location description, write that the policy does not mention inflation protection.',
    }),
    fieldOverride(policyPaymentType, {
      fieldDescription: `This is an indemnity policy return "indemnity". In the location description, write that the policy mentions cash indemnity beneits in the Narrative Summary.`,
    }),
  ],
  [FundingPolicyType.jointThriventLongTermCareInsurance]: [
    fieldOverride(policyMaximumBenefitAmount, {
      fieldTitle: 'Available Benefit Amount',
      fieldDescription: `This is the maximum amount available to spend on long-term care for both policyholders combined. Therefore, you need to sum the available benefit amounts for both policyholders. The amounts for each policyholder should be equal to each other.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `This is the monthly premium for both policyholders combined. Therefore, you need to sum the monthly premiums for both policyholders. The amounts for each policyholder are likely different. If the modal premium is annual, then divide it by 12 to get the monthly premium.`,
    }),
    policyLimitedPayYears,
    policyLumpSumPayment,
    policyPaymentType,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
  ],
  [FundingPolicyType.jointMutualOfOmahaMutualcare]: [
    fieldOverride(policyMaximumBenefitAmount, {
      fieldTitle: 'Policy Limit',
      fieldDescription: `This is the maximum amount available to spend on long-term care for both policyholders combined. Therefore, you need to sum the policy limits for both policyholders. The amounts for each policyholder should be equal to each other.`,
    }),
    ...getMaxBenefitBenefitPerPeriodFields([MaxBenefitPeriod.daily, MaxBenefitPeriod.monthly]),
    homeCareWaitingPeriodDays,
    facilityCareWaitingPeriodDays,
    policyPremiumStartYear,
    fieldOverride(policyPremiumMonthlyCost, {
      fieldDescription: `This is the monthly premium for both policyholders combined. Therefore, take the combined premium under the Total premium section. If this premium amount is annual ("Your annual premium"), then divide by 12 to get the combined monthly premium.`,
    }),
    policyLimitedPayYears,
    policyLumpSumPayment,
    policyPaymentType,
    policyInflationProtectionPercentage,
    policyInflationProtectionType,
  ],
};

const policyTypeFieldMapTyped = policyTypeFieldMap as Partial<
  Record<FundingPolicyType, PolicyPromptField<any>[]>
>;

export const getPolicyPromptFields = (policyType: FundingPolicyType): PolicyPromptField<any>[] => {
  // If the policy type exists in policyTypeFormFieldMap, return its fields
  if (policyTypeFieldMapTyped[policyType]) {
    return policyTypeFieldMapTyped[policyType]!;
  }

  // Find the policy definition in fundingPolicyTypeDefList
  const policyDef = fundingPolicyTypeDefList.find(def => def.value === policyType);

  // If the policy type is missing, check for its genericType and return those fields
  if (policyDef?.genericType && policyTypeFieldMapTyped[policyDef.genericType]) {
    return policyTypeFieldMapTyped[policyDef.genericType]!;
  }

  // If the policy type is missing, check for its calculationType and return those fields
  if (policyDef && policyTypeFieldMapTyped[policyDef.calculationType]) {
    return policyTypeFieldMapTyped[policyDef.calculationType]!;
  }

  // If neither is found, throw error
  throw new Error(`Invalid policy type, ${policyType}`);
};
